import { isMissingFolderMessage } from '@utils-global';
import { AxiosError } from 'axios';

/**
 * GRAPH API ERROR RETRY LOGIC
 *
 * This module determines which Graph API errors should be retried.
 *
 * FLOW:
 * 1. Graph API call fails with an error
 * 2. isTransientError() evaluates if error is temporary (worth retrying)
 * 3. If transient: Retry with exponential backoff (1s, 2s, 4s)
 * 4. If permanent: Fail immediately (no retry)
 */

/**
 * Error codes that indicate transient issues
 */
const TRANSIENT_ERROR_CODES = [
  // 404 - Item might be temporarily unavailable (replication lag, caching). Overloaded: the same
  // code reports a destination folder the mailbox doesn't have, vetoed by message below.
  'ErrorItemNotFound',
  'ErrorMailboxMoveInProgress', // 503 - Mailbox temporarily unavailable
  'ErrorMoveCopyFailed', // 500 - Might be able to resolve with a retry
  'ServiceUnavailable', // 503 - Service temporarily down
  'serviceUnavailable', // 503 - Graph's lower-cased variant
  'ErrorTimeoutExpired', // Timeout errors
  'gatewayTimeout', // 504 - Gateway timeout
  'ErrorServerBusy', // 503 - Server overloaded
  'ApplicationThrottled', // 429 - Request throttled by the mailbox
  'CommandConcurrencyLimitReached', // Mailbox concurrent-command limit hit - retry after backoff
  'TooManyRequests', // 429 - Request throttled
  'tooManyRequests', // 429 - Graph's lower-cased variant
];

/**
 * HTTP status codes that indicate transient issues
 */
const TRANSIENT_STATUS_CODES = [
  429, // Too many requests - throttled, safe to retry after backoff
  503, // Service unavailable - transient
  504, // Gateway timeout - transient
  0, // Network timeout - transient
];

/**
 * Determines if an error is transient and worth retrying with the same request
 *
 * DECISION FLOW:
 * 1. Check HTTP status code (503, 504, timeout) - Transient
 * 2. Check ErrorItemNotFound against the message: a missing destination folder - Permanent
 * 3. Check error code (ErrorMailboxMoveInProgress, ErrorItemNotFound) - Transient
 * 4. Otherwise - Permanent (don't retry)
 *
 * Step 2 sits between the two lookups on purpose. It has to override the code list,
 * since ErrorItemNotFound is on it, but not the status list: a throttled or
 * unavailable server is worth retrying whatever its body says, and short-circuiting
 * earlier would also skip the Retry-After the caller reads off a 429.
 *
 * TRANSIENT ERRORS (will retry):
 * - Network timeouts (status 0)
 * - Service unavailable (503)
 * - Gateway timeout (504)
 * - ErrorItemNotFound (replication lag), unless its message names the destination folder
 * - ErrorMailboxMoveInProgress (temporary)
 * - ErrorMoveCopyFailed (500) - Exchange fails a move intermittently and the same
 *   request often succeeds on the next attempt
 *
 * PERMANENT ERRORS (won't retry):
 * - ErrorItemNotFound naming the destination folder ("Default folder JunkEmail not
 *   found.") — the mailbox has no such folder, retrying can't help
 * - 404 ErrorNonExistentMailbox (mailbox doesn't exist)
 * - 400 BadRequest (malformed request)
 * - 500 Internal Server Error (server-side issue) — permanent ONLY when the
 *   response body carries no known transient error code; a 500 whose body code
 *   is in TRANSIENT_ERROR_CODES (e.g. CommandConcurrencyLimitReached or a
 *   lower-cased throttle code) is still retried
 *
 * @param error - The Axios error from the failed request
 * @param destinationFolderId - The folder the request asked to move to, when it has one
 * @returns true if this is a transient error worth retrying
 */
export const isTransientError = (error: AxiosError, destinationFolderId?: string): boolean => {
  const errorData = error.response?.data as { error?: { code?: string; message?: string } };
  const errorCode = errorData?.error?.code;
  const errorMessage = errorData?.error?.message;
  const statusCode = error.response?.status || (error.code === 'ECONNABORTED' ? 0 : undefined);

  // Check for transient status codes
  if (statusCode !== undefined && TRANSIENT_STATUS_CODES.includes(statusCode)) {
    return true;
  }

  // A destination folder the mailbox doesn't have is reported as ErrorItemNotFound, the same
  // code replication lag uses, and only the message tells the two apart. Scoped to that code so
  // an unrelated failure that happens to name the folder keeps its retries.
  if (
    errorCode === 'ErrorItemNotFound' &&
    isMissingFolderMessage(errorMessage, destinationFolderId)
  ) {
    return false;
  }

  // Check for transient error codes
  if (errorCode && TRANSIENT_ERROR_CODES.includes(errorCode)) {
    return true;
  }

  // Permanent error
  return false;
};

/**
 * Reads the server-provided Retry-After delay (in seconds) from a 429 response
 * so a throttled mailbox is given the time it asked for before we retry.
 */
export const getRetryAfterMs = (error: AxiosError): number | undefined => {
  const header = error.response?.headers?.['retry-after'];

  if (!header) {
    return undefined;
  }

  const seconds = Number(header);

  if (Number.isNaN(seconds) || seconds < 0) {
    return undefined;
  }

  return seconds * 1000;
};
